import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import {
  Modal,
  Table,
  Tag,
  Statistic,
  Row,
  Col,
  Spin,
  Empty,
  Typography,
  message
} from 'antd'
import {
  ImportOutlined,
  ExportOutlined,
  ProfileOutlined
} from '@ant-design/icons'
import { useMediaQuery } from 'react-responsive'
import { getInventoryProductLedger } from '../../api/inventory'

const { Text } = Typography

// 出庫/進貨類型 → 顯示文字 + 顏色
const TYPE_META = {
  initial: { label: '建檔初始庫存', color: 'gold' },
  purchase: { label: '進貨入庫', color: 'green' },
  customer_checkout: { label: '客戶結帳', color: 'blue' },
  employee_purchase: { label: '員工自購', color: 'purple' },
  public_use: { label: '公用消耗', color: 'orange' },
  gift: { label: '贈送', color: 'magenta' }
}

// 依紀錄取得標籤（手動調整依方向分增/減）
const entryMeta = (record) => {
  if (record.entryType === 'adjustment') {
    return record.direction === 'in'
      ? { label: '庫存調整（增）', color: 'cyan' }
      : { label: '庫存調整（減）', color: 'volcano' }
  }
  return TYPE_META[record.entryType] || { label: record.entryType, color: 'default' }
}

const fmtMoney = (v) =>
  v === null || v === undefined ? '-' : `NT$ ${Number(v).toLocaleString()}`

// 日期一律不折斷；時間放日期下方
const DateBlock = styled.div`
  line-height: 1.3;
  .ledger-date {
    white-space: nowrap;
    font-weight: 500;
  }
  .ledger-time {
    display: block;
    font-size: 12px;
    color: #999;
    white-space: nowrap;
  }
`

const renderDate = (date, time) => (
  <DateBlock>
    <span className='ledger-date'>{date || '-'}</span>
    {time ? <span className='ledger-time'>{time}</span> : null}
  </DateBlock>
)

// 進 / 出數量（含顏色與箭頭；作廢顯示刪除線灰字）
const QtyText = ({ record }) => {
  const isIn = record.direction === 'in'
  if (record.cancelled) {
    return (
      <Text
        delete
        style={{ color: '#bbb', whiteSpace: 'nowrap' }}
        title='此筆已作廢，不影響結存'
      >
        {isIn ? '+' : '-'}
        {record.quantity}
      </Text>
    )
  }
  return (
    <Text
      strong
      style={{ color: isIn ? '#52c41a' : '#ff4d4f', whiteSpace: 'nowrap' }}
    >
      {isIn ? (
        <ImportOutlined style={{ marginRight: 4 }} />
      ) : (
        <ExportOutlined style={{ marginRight: 4 }} />
      )}
      {isIn ? '+' : '-'}
      {record.quantity}
    </Text>
  )
}

// 來源 / 對象文字
const sourceText = (record) => {
  if (record.entryType === 'initial') return '商品建檔'
  if (record.entryType === 'adjustment') {
    return record.reason ? `原因：${record.reason}` : '手動調整'
  }
  if (record.direction === 'in') {
    return record.operatorName ? `進貨人員：${record.operatorName}` : '-'
  }
  if (record.customerName) return `客人：${record.customerName}`
  if (record.employeeName) return `員工：${record.employeeName}`
  return '-'
}

/* ===================== 手機版卡片 ===================== */

const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const LedgerCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
  background: ${(props) => (props.$cancelled ? '#fafafa' : '#fff')};
  opacity: ${(props) => (props.$cancelled ? 0.7 : 1)};
`

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  padding: 2px 0;
`

const CardLabel = styled.span`
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
`

const CardValue = styled.span`
  color: #333;
  text-align: right;
  word-break: break-word;
`

const ProductLedgerModal = ({ open, productId, productName, onClose }) => {
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const fetchLedger = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    try {
      const response = await getInventoryProductLedger(productId)
      const payload = response.data?.data || response.data || {}
      setData(payload)
    } catch (error) {
      console.error('獲取商品異動明細失敗:', error)
      message.error(
        error.response?.data?.message || '獲取商品異動明細失敗，請稍後再試'
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    if (open && productId) {
      fetchLedger()
    }
    if (!open) {
      setData(null)
    }
  }, [open, productId, fetchLedger])

  const showCost = data?.showCost
  const unit = data?.unit || '件'

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date, record) => renderDate(date, record.time)
    },
    {
      title: '類型',
      key: 'entryType',
      width: 130,
      render: (_, record) => {
        const meta = entryMeta(record)
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <Tag color={record.cancelled ? 'default' : meta.color}>
              {meta.label}
            </Tag>
            {record.cancelled ? <Tag color='red'>作廢</Tag> : null}
          </span>
        )
      }
    },
    {
      title: '來源 / 對象',
      key: 'source',
      render: (_, record) => (
        <div style={record.cancelled ? { color: '#bbb' } : undefined}>
          <div>{sourceText(record)}</div>
          {record.note ? (
            <Text type='secondary' style={{ fontSize: 12 }}>
              備註：{record.note}
            </Text>
          ) : null}
          {record.cancelled && record.cancelReason ? (
            <Text type='secondary' style={{ fontSize: 12 }}>
              作廢原因：{record.cancelReason}
            </Text>
          ) : null}
        </div>
      )
    },
    {
      title: '進 / 出',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (_, record) => <QtyText record={record} />
    },
    {
      title: '結存',
      dataIndex: 'balance',
      key: 'balance',
      width: 90,
      align: 'right',
      render: (b, record) =>
        record.cancelled ? (
          <Text type='secondary'>—</Text>
        ) : (
          <Text strong style={{ whiteSpace: 'nowrap' }}>
            {b} {unit}
          </Text>
        )
    }
  ]

  if (showCost) {
    columns.push({
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (amount) => (
        <span style={{ whiteSpace: 'nowrap' }}>{fmtMoney(amount)}</span>
      )
    })
  }

  const entries = data?.entries || []
  const openingNonZero = data && Number(data.openingBalance) !== 0

  const renderSummary = () => (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={12} sm={6}>
        <Statistic title='現有庫存' value={data.currentStock} suffix={unit} />
      </Col>
      <Col xs={12} sm={6}>
        <Statistic
          title='期初結存'
          value={data.openingBalance}
          suffix={unit}
          valueStyle={openingNonZero ? { color: '#faad14' } : undefined}
        />
      </Col>
      <Col xs={12} sm={6}>
        <Statistic
          title='累計進貨'
          value={data.totalIn}
          suffix={unit}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col xs={12} sm={6}>
        <Statistic
          title='累計出庫'
          value={data.totalOut}
          suffix={unit}
          valueStyle={{ color: '#ff4d4f' }}
        />
      </Col>
    </Row>
  )

  const renderMobileList = () => {
    if (entries.length === 0) {
      return <Empty description='此商品尚無進出庫紀錄' />
    }
    return (
      <MobileList>
        {entries.map((record, idx) => {
          const meta = entryMeta(record)
          return (
            <LedgerCard
              key={`${record.sourceId || 'r'}-${idx}`}
              $cancelled={record.cancelled}
            >
              <CardTop>
                <span>
                  <Tag color={record.cancelled ? 'default' : meta.color}>
                    {meta.label}
                  </Tag>
                  {record.cancelled ? <Tag color='red'>作廢</Tag> : null}
                </span>
                <QtyText record={record} />
              </CardTop>
              <CardRow>
                <CardLabel>日期</CardLabel>
                <CardValue>{renderDate(record.date, record.time)}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>來源 / 對象</CardLabel>
                <CardValue>{sourceText(record)}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>結存</CardLabel>
                <CardValue>
                  {record.cancelled ? (
                    <Text type='secondary'>—</Text>
                  ) : (
                    <Text strong>
                      {record.balance} {unit}
                    </Text>
                  )}
                </CardValue>
              </CardRow>
              {showCost && (
                <CardRow>
                  <CardLabel>金額</CardLabel>
                  <CardValue>{fmtMoney(record.amount)}</CardValue>
                </CardRow>
              )}
              {record.note ? (
                <CardRow>
                  <CardLabel>備註</CardLabel>
                  <CardValue>{record.note}</CardValue>
                </CardRow>
              ) : null}
              {record.cancelled && record.cancelReason ? (
                <CardRow>
                  <CardLabel>作廢原因</CardLabel>
                  <CardValue>{record.cancelReason}</CardValue>
                </CardRow>
              ) : null}
            </LedgerCard>
          )
        })}
      </MobileList>
    )
  }

  return (
    <Modal
      title={
        <span>
          <ProfileOutlined style={{ marginRight: 8 }} />
          商品異動明細 — {productName || data?.productName || ''}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100%' : 920}
      style={
        isMobile
          ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }
          : undefined
      }
      styles={
        isMobile
          ? {
              body: {
                paddingTop: 12,
                paddingLeft: 12,
                paddingRight: 12,
                paddingBottom: 12,
                maxHeight: 'calc(100vh - 56px)',
                overflowY: 'auto'
              },
              header: { paddingLeft: 12, paddingRight: 12 },
              content: { borderRadius: 0, minHeight: '100vh' }
            }
          : undefined
      }
      destroyOnClose
    >
      <Spin spinning={loading}>
        {data ? (
          <>
            {renderSummary()}

            {isMobile ? (
              renderMobileList()
            ) : (
              <Table
                rowKey={(record, idx) =>
                  `${record.sourceId || 'r'}-${record.entryType}-${idx}`
                }
                columns={columns}
                dataSource={entries}
                size='small'
                rowClassName={(record) =>
                  record.cancelled ? 'ledger-row-cancelled' : ''
                }
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: <Empty description='此商品尚無進出庫紀錄' />
                }}
              />
            )}
          </>
        ) : (
          !loading && <Empty description='暫無資料' />
        )}
      </Spin>
    </Modal>
  )
}

export default ProductLedgerModal
